import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-95',
  {
    variants: {
      variant: {
        default: 'bg-gradient-to-b from-primary via-primary to-primary/90 text-primary-foreground shadow-lg hover:shadow-xl hover:shadow-primary/20 border border-primary-foreground/20',
        destructive: 'bg-gradient-to-b from-destructive to-destructive/90 text-destructive-foreground shadow-lg hover:shadow-xl hover:shadow-destructive/20',
        outline: 'border-2 border-border bg-transparent shadow-sm hover:bg-secondary hover:border-accent/50',
        secondary: 'bg-secondary text-secondary-foreground shadow-md hover:bg-secondary/80 border border-border/40',
        ghost: 'hover:bg-accent/10 hover:text-accent',
        link: 'text-primary underline-offset-4 hover:underline',
        game: 'pixel-button',
        correct: 'bg-gradient-to-b from-game-correct to-game-correct/90 text-white shadow-lg hover:shadow-xl hover:shadow-game-correct/30',
        wrong: 'bg-gradient-to-b from-game-wrong to-game-wrong/90 text-white shadow-lg hover:shadow-xl hover:shadow-game-wrong/30',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-8 rounded-lg px-3 text-xs',
        lg: 'h-11 rounded-lg px-8 text-base font-semibold',
        icon: 'h-10 w-10',
        game: 'h-12 px-6 py-3 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
